import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import {
  InfiniteData,
  keepPreviousData,
  QueryKey,
  UseInfiniteQueryResult,
  useInfiniteQuery as useReactQueryInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useState } from 'react';

interface PageData {
  total: number;
  currentPage: number;
  perPage: number;
  lastPage: number;
}

interface WithPageData {
  pageData: PageData;
}

type ArrayElement<T> = T extends Array<infer U> ? U : never;

type InfiniteQueryResult<R, K extends keyof R> = {
  [P in K]: R[P];
} & {
  total: number;
  fetchNextPage: () => Promise<void>;
  loadAll: () => Promise<void>;
  isLoading: boolean;
  unloadedContentCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  paginationModel: GridPaginationModel;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  rawResult: UseInfiniteQueryResult<InfiniteData<AxiosResponse<R, unknown>, unknown>, Error>;
  error?: AxiosError;
  mutateItem: <T extends ArrayElement<R[K]>>(
    predicate: (item: ArrayElement<R[K]>) => boolean,
    updater: (item: ArrayElement<R[K]>) => T
  ) => void;
};

export function useInfiniteQuery<
  P extends { page?: number; perPage?: number },
  R extends WithPageData,
  K extends keyof R = keyof R,
>({
  queryKey,
  queryFunction,
  queryParams,
  options,
  dataKey = 'contents' as K,
}: {
  queryKey: QueryKey | ((parameters: P) => QueryKey);
  queryFunction: (parameters: P) => Promise<AxiosResponse<R>>;
  queryParams?: P;
  options?: {
    page?: number;
    perPage?: number;
    enabled?: boolean;
  };
  dataKey?: K;
}): InfiniteQueryResult<R, K> {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: options?.page ?? 0,
    pageSize: options?.perPage ?? DATA_GRID_LAZY_PAGE_SIZE,
  });
  const queryClient = useQueryClient();

  const result = useReactQueryInfiniteQuery({
    queryKey: typeof queryKey === 'function' ? queryKey(queryParams ?? ({} as P)) : queryKey,
    queryFn: ({ pageParam: pageParameter = 1 }) =>
      queryFunction({
        ...queryParams,
        page: pageParameter,
        perPage: paginationModel.pageSize,
      } as P),
    getNextPageParam: lastPage => {
      const pageData = lastPage.data.pageData;
      if (!pageData || typeof pageData !== 'object') return;
      const { currentPage, lastPage: totalPages } = pageData;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: options?.enabled,
  });

  const data: R[K] = (result.data?.pages ?? []).reduce<ArrayElement<R[K]>[]>(
    (accumulator, page) => [...accumulator, ...((page.data[dataKey] ?? []) as ArrayElement<R[K]>[])],
    []
  ) as R[K];
  const total = result.data?.pages[0]?.data.pageData.total ?? 0;

  const fetchNextPage = async () => {
    if (result.hasNextPage) {
      await result.fetchNextPage();
    }
  };

  const loadAll = async () => {
    while (result.hasNextPage) {
      await result.fetchNextPage();
    }
  };

  const mutateItem = <T extends ArrayElement<R[K]>>(
    predicate: (item: ArrayElement<R[K]>) => boolean,
    updater: (item: ArrayElement<R[K]>) => T
  ) => {
    const currentQueryKey = typeof queryKey === 'function' ? queryKey(queryParams ?? ({} as P)) : queryKey;

    queryClient.setQueryData<InfiniteData<AxiosResponse<R, unknown>, unknown>>(currentQueryKey, oldData => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          data: {
            ...page.data,
            [dataKey]: (page.data[dataKey] as ArrayElement<R[K]>[]).map(item =>
              predicate(item) ? updater(item) : item
            ),
          },
        })),
      };
    });
  };

  return {
    [dataKey]: data,
    total,
    fetchNextPage,
    loadAll,
    isLoading: result.isLoading,
    unloadedContentCount: total - (Array.isArray(data) ? data.length : 0),
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    paginationModel,
    setPaginationModel,
    rawResult: result,
    error: result.error,
    mutateItem,
  } as InfiniteQueryResult<R, K>;
}
